import { useEffect, useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import { api } from '../../api';
import {
  highlightSnippet,
  CITE_CLASS,
  clearMarks,
} from '../preview/highlightSnippet';
import {
  logHelpArticleOpen,
  logHelpArticleHighlight,
} from './helpCitationDebug';

/**
 * Slugify a markdown heading for ``<h2 id="...">``. Matches the anchor
 * format the backend ingestor emits (extraction.units[].source_ref.section_anchor)
 * and the help-citation link builder in sourceRef.js.
 */
function slugifyAnchor(text) {
  if (!text) return 'section';
  return String(text)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '') || 'section';
}

/**
 * Help article renderer — shared between the standalone /help/<slug> page
 * (HelpDoc) and the in-drawer view (HelpChatPanel's article mode).
 *
 * Props
 * -----
 * slug             string   Article slug to fetch via /help/docs/<slug>.
 * anchor?          string   Section anchor to scroll to on open.
 * queryHighlight?  string   Verbatim snippet to highlight inside the body.
 * viewedInDrawer?  bool     Threaded into help.article.open for analytics
 *                           — true when the drawer is rendering inline.
 * onSlugLink?      fn(slug) Called when the user clicks an in-article slug
 *                           link (e.g. ``[Asking Arc](asking-arc)``). The
 *                           drawer uses this to navigate in-drawer; the
 *                           standalone page passes undefined and lets the
 *                           default react-router Link behavior take over.
 * SlugLink?        React    A component that renders an internal slug link
 *                           (used by the standalone page to delegate to
 *                           react-router's Link). Falls back to a plain
 *                           <a href> when omitted.
 */
export default function HelpArticleView({
  slug,
  anchor,
  queryHighlight,
  viewedInDrawer = false,
  onSlugLink,
  SlugLink,
}) {
  const [doc, setDoc] = useState(null);
  const [error, setError] = useState(null);
  const bodyRef = useRef(null);

  // Re-fetch when slug changes (in-drawer navigation between articles).
  useEffect(() => {
    setDoc(null);
    setError(null);
    api.get(`/help/docs/${slug}`)
      .then(setDoc)
      .catch((e) => setError(e.message));
  }, [slug]);

  // After the article renders: scroll to anchor + highlight snippet.
  // We defer with setTimeout so the h2 ids are mounted before we query.
  useEffect(() => {
    if (!doc || !bodyRef.current) return;

    const t = setTimeout(() => {
      // Surface 2 — log the article-open event with citation params.
      logHelpArticleOpen({
        slug,
        anchor,
        hasSnippet: !!queryHighlight,
        snippetChars: queryHighlight?.length || 0,
        viewedInDrawer,
      });

      // Scroll to anchor.
      if (anchor && bodyRef.current) {
        const target = bodyRef.current.querySelector(`#${CSS.escape(anchor)}`);
        if (target) {
          target.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }

      // Highlight the snippet if one was passed.
      if (queryHighlight && bodyRef.current) {
        clearMarks(bodyRef.current, CITE_CLASS);
        const marks = highlightSnippet(
          bodyRef.current,
          queryHighlight,
          CITE_CLASS,
          { scroll: !anchor }, // anchor scroll wins; otherwise scroll to highlight
        );
        logHelpArticleHighlight({
          slug,
          snippetChars: queryHighlight.length,
          marksCount: marks?.length || 0,
          matched: (marks?.length || 0) > 0,
          viewedInDrawer,
        });
      }
    }, 50);

    return () => clearTimeout(t);
  }, [doc, slug, anchor, queryHighlight, viewedInDrawer]);

  if (error) {
    return <p style={{ color: 'var(--color-danger)' }}>{error}</p>;
  }
  if (!doc) {
    return <p>Loading…</p>;
  }

  return (
    <>
      <h1 className="help-doc-title">{doc.title}</h1>
      <div className="help-doc-body" ref={bodyRef}>
        <ReactMarkdown
          components={{
            // h2 → id from slugified heading so anchor links land.
            h2: ({ children }) => {
              const headingText = Array.isArray(children)
                ? children.map(c => (typeof c === 'string' ? c : '')).join('')
                : String(children || '');
              return <h2 id={slugifyAnchor(headingText)}>{children}</h2>;
            },
            // Internal slug links: delegate to the caller. SlugLink wins
            // (standalone page passes react-router's Link). Else fire the
            // onSlugLink callback (drawer pushes a new article state).
            // Else plain anchor (no SPA available — shouldn't happen).
            a: ({ href, children }) => {
              const isInternalSlug = href && /^[a-z0-9-]+$/.test(href);
              if (isInternalSlug && SlugLink) {
                return <SlugLink slug={href}>{children}</SlugLink>;
              }
              if (isInternalSlug && onSlugLink) {
                return (
                  <a
                    href={`/help/${href}`}
                    onClick={(e) => { e.preventDefault(); onSlugLink(href); }}
                  >
                    {children}
                  </a>
                );
              }
              return <a href={href}>{children}</a>;
            },
          }}
        >
          {doc.body}
        </ReactMarkdown>
      </div>
      <div className="help-doc-footer">
        Last verified: {doc.last_verified?.date || 'unknown'}
      </div>
    </>
  );
}
